"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, MapPin, ShieldCheck, PhoneCall, Truck, Wrench, CircleCheck as CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyHighlights = [{
  title: "25-30 Min Average Response",
  description: "Dedicated mobile service trucks stationed near major routes ensure rapid dispatch across Ellenwood.",
  icon: Clock
}, {
  title: "Clayton & DeKalb Border Coverage",
  description: "Centrally positioned to seamlessly handle homes and businesses straddling county lines.",
  icon: MapPin
}, {
  title: "Fully Stocked Master Units",
  description: "Our vans arrive loaded with commercial-grade replacement valves, pipe sections, and clearing gear.",
  icon: Truck
}, {
  title: "Licensed & Guaranteed Work",
  description: "Every repair is carried out by licensed plumbers and backed by standard workmanship guarantees.",
  icon: ShieldCheck
}];
const commonServices = ["Emergency Burst Pipe & Leak Isolation", "Main Water Line & Sewer Backups", "Tank & Tankless Water Heater Failures", "Clogged Drain & Hydro Jetting", "Commercial & Residential Fixture Repairs", "Slab Leak Detection & Line Tracing"];
const dispatchPillars = [{
  label: "Dispatch Base",
  value: "Minutes from I-675 & GA-42"
}, {
  label: "Availability",
  value: "24 Hours / 7 Days a Week"
}, {
  label: "Pricing Structure",
  value: "Upfront Estimates, No Hidden Fees"
}, {
  label: "Service Radius",
  value: "All Ellenwood ZIP codes (30294 & surrounding)"
}];
export default function EllenwoodArea() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <section id="ellenwoodservicearea" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header Section */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={fadeIn} className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 font-semibold text-xs tracking-wide uppercase">
              <Sparkles className="size-3 text-accent" />
              Service Territory Spotlight
            </Badge>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-4">
            Fast Emergency Plumbing in <span className="text-primary">Ellenwood, GA</span>
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Conveniently situated right on the Clayton and DeKalb county corridor, our emergency service teams provide dependable 25 to 30 minute dispatch times for both urgent repairs and scheduled maintenance.
          </p>
        </motion.div>

        {/* Main Grid: Info + Highlights */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Left Column: Local Expertise Detail Card */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeIn} className="lg:col-span-7 flex flex-col gap-6">
            <Card className="border border-border bg-card shadow-sm rounded-xl overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary text-sm font-semibold mb-1">
                  <MapPin className="size-4 shrink-0 text-accent" />
                  <span>Strategic Regional Positioning</span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                  Rapid Dispatch from Nearby Facilities
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm font-default">
                  Serving residential neighborhoods, retail complexes, and commercial parks around Fairview Road, Anvil Block Road, and Flakes Mill Road.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-foreground/90 leading-relaxed font-default text-sm sm:text-base">
                  When a water heater bursts or a main line overflows, proximity is everything. Because our service fleet operates around the Clayton-DeKalb line, we bypass distant metro congestion to reach your Ellenwood property rapidly with the required tools on board.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {dispatchPillars.map((pillar, i) => <div key={pillar.label} data-index={i} className="p-3.5 rounded-lg bg-background border border-border flex flex-col justify-center">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {pillar.label}
                      </span>
                      <span className="text-sm font-semibold text-foreground mt-0.5">
                        {pillar.value}
                      </span>
                    </div>)}
                </div>

                <Separator />

                <div>
                  <h3 className="text-base font-semibold text-foreground font-default mb-3 flex items-center gap-2">
                    <Wrench className="size-4 text-primary" />
                    Priority Plumbing Services in Ellenwood
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {commonServices.map((svc, i) => <div key={svc} data-index={i} className="flex items-start gap-2 text-sm text-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="font-default">{svc}</span>
                      </div>)}
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm">
                    <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call 24/7 Dispatch</span>
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                    <Link to="/contact/" className="flex items-center justify-center gap-2">
                      <span>Request Inspection</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: 4 Key Features Grid */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={staggerContainer} className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            {keyHighlights.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} variants={fadeIn} data-index={i}>
                  <Card className="border border-border bg-card shadow-sm rounded-xl transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
                        <IconComp className="size-5" />
                      </div>
                      <div>
                        <h4 className="text-base font-semibold font-default text-card-foreground mb-1">
                          {item.title}
                        </h4>
                        <p className="text-sm text-muted-foreground font-default leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Bottom Location CTA Strip */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={fadeIn} className="rounded-2xl bg-secondary p-6 sm:p-8 lg:p-10 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl text-center md:text-left">
            <span className="text-xs uppercase font-bold tracking-wider text-secondary-foreground/70 mb-1 block">
              Immediate Assistance Available
            </span>
            <h3 className="text-xl sm:text-2xl font-bold font-default text-secondary-foreground mb-2">
              Plumbing emergency at your Ellenwood property?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-default">
              Our nearby service technicians are on call 24 hours a day to diagnose, isolate, and repair plumbing failures before they cause extensive property damage.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/emergency-plumbing/" className="flex items-center justify-center gap-2">
                <span>Emergency Services</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/service-areas/">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}